import { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { trackAddToCart, trackRemoveFromCart } from '../utils/gtmDataLayer';

// Cart context
const CartContext = createContext();

// Cart actions
const CART_ACTIONS = {
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  CLEAR_CART: 'CLEAR_CART',
  LOAD_CART: 'LOAD_CART'
};

const normalizePriceValue = (price) => {  
  if (price && typeof price === 'object') {
    if (typeof price.price === 'number') { 
      return price.price;     
    }
    if (typeof price.min === 'number') {  
      return price.min; 
    }
    if (typeof price.regular === 'number') {     
      return price.regular; 
    }
    if (typeof price.value === 'number') {  
      return price.value;    
    }  
  }
  if (typeof price === 'number') {
    return price;   
  }
  const parsed = parseFloat(String(price || '').replace(/[^0-9.]/g, ''));     
  return Number.isFinite(parsed) ? parsed : 0;  
};  

// Cart reducer 
const cartReducer = (state, action) => { 
  switch (action.type) {   
    case CART_ACTIONS.ADD_ITEM: {    
      const productId = action.payload.id;
      const productName = action.payload.name;    
      
      // Check for existing item by ID or name (fallback)  
      const existingItem = state.items.find(item => 
        item.id === productId || 
        (item.name === productName && item.strength === action.payload.strength) 
      );
      
      if (existingItem) {     
        // Update quantity if item already exists
        return {
          ...state,
          items: state.items.map(item =>           
            item.id === existingItem.id    
              ? { ...item, quantity: item.quantity + 1 }      
              : item
          )
        };   
      } else {
        // Add new item
        return {
          ...state,
          items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }]
        };
      }
    }

    case CART_ACTIONS.REMOVE_ITEM:
      return { 
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case CART_ACTIONS.UPDATE_QUANTITY:  
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item
        ).filter(item => item.quantity > 0)
      };

    case CART_ACTIONS.CLEAR_CART:
      return {
        ...state,
        items: []
      };

    case CART_ACTIONS.LOAD_CART:
      return {
        ...state,
        items: action.payload || []
      };

    default:
      return state;
  }
};

// Get initial state from localStorage
const getInitialState = () => {
  try {
    const savedCart = localStorage.getItem('pillpointuk-cart');
    if (savedCart) {
      const cartData = JSON.parse(savedCart);
      return { items: cartData };
    }
  } catch (error) {
    localStorage.removeItem('pillpointuk-cart');
  }
  return { items: [] };       
};

// Initial cart state
const initialState = getInitialState();

// Cart provider component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Cart is now loaded in initial state, no need for useEffect

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('pillpointuk-cart', JSON.stringify(state.items));
    } catch (error) {
      // Error saving cart
    }
  }, [state.items]);

  // Open/Close cart sidebar
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  // Helper functions
  const addToCart = (product, openSidebar = true) => {
    // Ensure product has a unique ID
    const productWithId = {
      ...product,
      id: product.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      addedAt: Date.now() // Add timestamp for debugging
    };
    
    dispatch({ type: CART_ACTIONS.ADD_ITEM, payload: productWithId });
    
    // GTM: Track add to cart event
    trackAddToCart(productWithId, 1);
    
    // Open cart sidebar after adding item 
    if (openSidebar) {
      setIsCartOpen(true);
    }
  };

  const removeFromCart = (productId) => {
    // Find the product being removed for GTM tracking
    const removedProduct = state.items.find(item => item.id === productId);
    
    dispatch({ type: CART_ACTIONS.REMOVE_ITEM, payload: productId });
    
    // GTM: Track remove from cart event
    if (removedProduct) {
      trackRemoveFromCart(removedProduct, removedProduct.quantity || 1);
    }
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: CART_ACTIONS.UPDATE_QUANTITY, payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: CART_ACTIONS.CLEAR_CART });
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => {
      const priceValue = normalizePriceValue(item.price ?? item.pricing);
      return total + priceValue * (item.quantity || 1);
    }, 0);
  };

  const getCartCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  // Alias for getCartTotal (used in Header component)
  const getTotalAmount = () => {
    return getCartTotal();
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getTotalAmount,
    getCartCount,
    isCartOpen,
    openCart,
    closeCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;